import {
  Accordion,
  Box,
  Link,
  List,
  Separator,
  Text,
  VStack
} from "@chakra-ui/react"
import { getBlockTypeLabel } from "@pathwright/blocks-core"
import {
  BlocksChangeBlockItem,
  BlocksChanges as BlocksChangesType,
  useBlocksConfig,
  useEditorStateSelector
} from "@pathwright/blocks-engine/"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { useEffect, useState } from "react"

import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"

import When from "@pathwright/ui/src/components/when/When"

export type OnClickBlockChange = (blockID: string) => void

const BlocksChanges = ({
  changes,
  onClickBlockChange
}: {
  changes: BlocksChangesType["blocks"]
  onClickBlockChange?: OnClickBlockChange
}) => {
  const { tc, t } = useTranslate()

  const sortedChanges = [...changes].sort(
    (a: BlocksChangeBlockItem, b: BlocksChangeBlockItem) => {
      // Place reordered change at the end
      if (a.type === "reordered" && b.type !== "reordered") return 1
      if (a.type !== "reordered" && b.type === "reordered") return -1
      return (a?.data?.order || 0) - (b?.data?.order || 0)
    }
  )

  if (!sortedChanges.length) return null

  return (
    <VStack align="left" gap={0.5} my={2}>
      <Text textAlign="left" fontSize="sm" fontWeight="700" m={0} p={0}>
        {tc("Draft changes")}:
      </Text>
      <List.Root as="ul" marginInlineStart="1em">
        {sortedChanges.map((change: BlocksChangeBlockItem, index: number) => {
          const { type: changeType, data } = change

          const label = getBlockTypeLabel(data.type, data.layout)

          const renderChangeContent = () => {
            if (changeType === "reordered") {
              return <Box as="i">{t("blocks were reordered")}</Box>
            }

            const blockChangeLabel = `${changeType} ${label}`

            if (onClickBlockChange) {
              return (
                <Link onClick={() => onClickBlockChange(data.id)}>
                  {blockChangeLabel}
                </Link>
              )
            }

            return blockChangeLabel
          }

          return (
            <List.Item
              ml={2}
              lineHeight="1.2"
              key={data.id || `block-change-${index}`}
            >
              <Text fontSize="sm" color="gray.600" m={0} p={0} textAlign="left">
                {renderChangeContent()}
              </Text>
            </List.Item>
          )
        })}
      </List.Root>
    </VStack>
  )
}

const ContentPublishDiff = ({
  changes: propsChanges,
  onClickBlockChange
}: {
  changes?: BlocksChangesType
  onClickBlockChange?: OnClickBlockChange
}) => {
  const { tc, t, Trans } = useTranslate()
  const [expanded, setExpanded] = useState(false)
  const stateChanges = useEditorStateSelector((state) => state.changes)
  const createdBy = useEditorStateSelector((state) => state.createdBy)
  const { getUserByID } = useBlocksConfig()
  const [creator, setCreator] = useState<any>(null)
  const [updaters, setUpdaters] = useState<any[]>([])

  const changes = propsChanges || stateChanges

  const handleGetUsers = async () => {
    if (!getUserByID) return

    if (createdBy) {
      const creator = await getUserByID(Number(createdBy))
      if (!creator?.data) return

      const user = creator.data.user
      setCreator(user)
    }

    const sortedBlocks = changes?.blocks?.length
      ? [...changes!.blocks].sort(
          (a: BlocksChangeBlockItem, b: BlocksChangeBlockItem) =>
            (b?.data?.lastModifiedDateTime || 0) -
            (a?.data?.lastModifiedDateTime || 0)
        )
      : []

    const updaters = await Promise.all(
      sortedBlocks
        .map(async (blockChange: BlocksChangeBlockItem) => {
          if (!blockChange.data.lastModifiedBy) return null

          const updater = await getUserByID(
            Number(blockChange.data.lastModifiedBy)
          )
          if (!updater?.data) return null

          return updater.data.user
        })
        .filter(Boolean)
    )

    // Filter out null values and enforce uniqueness by `id`
    const uniqueUpdaters = Array.from(
      new Map(
        updaters.filter(Boolean).map((updater) => [updater.id, updater])
      ).values()
    )

    setUpdaters(uniqueUpdaters)
  }

  const formattedUpdaters = updaters
    .map((updater: any, index: number) => {
      if (index === 0 || !updater) return null

      const isLast = index === updaters.length - 1
      const isPenultimate = index === updaters.length - 2
      const conjunction = isLast ? "" : isPenultimate ? " and " : ", "

      return ` ${updater.display_name}${conjunction}`
    })
    .filter(Boolean)
    .join("")

  useEffect(() => {
    if (changes) {
      handleGetUsers()
    }
  }, [changes])

  if (!changes) return null

  return (
    <VStack
      bgColor={expanded ? "gray.100" : "transparent"}
      shadow={expanded ? "md" : "none"}
      borderRadius={expanded ? "md" : "none"}
      py={expanded ? 2 : 0}
      px={4}
      my={2}
      mx="auto"
      w="max-content"
      minW="200px"
      maxW="100%"
    >
      <Accordion.Root collapsible value={expanded ? ["0"] : []}>
        <Accordion.Item value="0" border="none">
          <Accordion.ItemTrigger
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation()
              setExpanded(!expanded)
            }}
            py={1}
            bg={"transparent"}
            _hover={{
              bgColor: "transparent"
            }}
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={2}
            w="full"
          >
            <Pathicon
              icon="clock-fill"
              color="rgb(170, 170, 170)"
              size="1.2em"
            />
            <Link
              textDecoration="underline"
              onClick={(e) => e.preventDefault()}
              color="rgb(170, 170, 170)"
              fontSize="sm"
              fontWeight="600"
            >
              {tc("View {{changes}}", {
                changes: changes.blocks?.length
                  ? `${changes.blocks?.length} ${t("change", {
                      count: changes.blocks?.length
                    })}`
                  : t("history")
              })}
              ...
            </Link>
          </Accordion.ItemTrigger>
          <Accordion.ItemContent cursor="default" p={0}>
            <VStack align="left">
              {changes.blocks && (
                <BlocksChanges
                  onClickBlockChange={onClickBlockChange}
                  changes={changes.blocks}
                />
              )}
              <Separator
                borderColor="gray.200"
                opacity="1"
                borderBottomWidth={0}
                borderTopWidth="1px"
              />
              <VStack align="left" gap={0.5} py={2}>
                {changes.lastModifiedDateTime ? (
                  <>
                    <Text
                      textAlign="left"
                      fontSize="xs"
                      color="gray.500"
                      lineHeight="1.3"
                      m={0}
                      p={0}
                      gap={1}
                      css={{
                        "& .When, & .TooltipWrapper": {
                          display: "inline"
                        }
                      }}
                    >
                      <Trans i18nKey="last_updated_by">
                        Last updated{" "}
                        <When
                          date={new Date(changes.lastModifiedDateTime)}
                          fitContent
                        />{" "}
                        {updaters.length > 0
                          ? t(`by {{updater}}`, {
                              updater: updaters[0].display_name
                            })
                          : ""}
                      </Trans>
                    </Text>
                    {updaters.length > 1 ? (
                      <Text
                        textAlign="left"
                        fontSize="xs"
                        color="gray.500"
                        lineHeight="1.3"
                        m={0}
                        p={0}
                        gap={1}
                        css={{
                          "& .When, & .TooltipWrapper": {
                            display: "inline"
                          }
                        }}
                        whiteSpace="wrap"
                      >
                        <Trans
                          i18nKey="other_modifications_from"
                          values={{
                            updaters: formattedUpdaters
                          }}
                        >
                          {" "}
                          Other modifications from {formattedUpdaters}
                        </Trans>
                      </Text>
                    ) : null}
                    <Separator
                      my={1}
                      borderBottomWidth={0}
                      borderTopWidth="1px"
                      w="full"
                    />
                  </>
                ) : null}
                {changes.lastPublishedDateTime ? (
                  <Text
                    textAlign="left"
                    fontSize="xs"
                    color="gray.500"
                    lineHeight="1.3"
                    m={0}
                    p={0}
                    gap={1}
                    css={{
                      "& .When, & .TooltipWrapper": {
                        display: "inline"
                      }
                    }}
                  >
                    <Trans i18nKey="last_saved">
                      Last saved{" "}
                      <When
                        date={new Date(changes.lastPublishedDateTime)}
                        fitContent
                      />
                    </Trans>
                  </Text>
                ) : null}
                <Text
                  textAlign="left"
                  fontSize="xs"
                  color="gray.500"
                  lineHeight="1.3"
                  m={0}
                  p={0}
                  gap={1}
                  css={{
                    "& .When, & .TooltipWrapper": {
                      display: "inline"
                    }
                  }}
                >
                  <Trans
                    i18nKey="Created <When>{{when}}</When> by {{creator}}"
                    components={{
                      When: (
                        <When
                          date={new Date(changes.createdDateTime)}
                          fitContent
                        />
                      )
                    }}
                    values={{
                      when: new Date(changes.createdDateTime),
                      creator: creator?.display_name
                    }}
                  />
                </Text>
              </VStack>
            </VStack>
          </Accordion.ItemContent>
        </Accordion.Item>
      </Accordion.Root>
    </VStack>
  )
}

export default ContentPublishDiff
